import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { OnboardingBaseTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { CallInsightApi } from '@frontend/call-insight-panel';
import { DemoContactPanelComponent } from '@frontend/contact-panel';
import { useDemoPanelShallowStore } from '@frontend/demo-panels';
import { WIDTH_DIMS } from '@frontend/panel-engine';
import type { RecentCallRecord, RecentCallRecords } from '@frontend/phone-panels';
import { useOnboardingShallowStore } from '@frontend/product-onboarding';
import { useAppScopeStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { CI_BASE_ONBOARDING, CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS } from '../constant';
import { findBestEligibleCall, findBestEligibleCallWithContact } from '../utils/call-eligibility';
import { getItemIndex, getItemPageNumber } from '../utils/pagination-utils';
import { useOnboardingCallActionShallowStore } from './use-onboarding-call-action-store';
import { usePaginatedItemSearch } from './use-paginated-item-search';

interface UseCallInsightEligibleHotspotParams {
  tableData: RecentCallRecord[];
  currentPage: number;
  allPages?: RecentCallRecords[];
  hasNextPage?: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  openCallInsightPanel?: CallInsightApi['open'];
  onSearchExhausted?: (task: OnboardingBaseTask) => void;
  onPatientContextResolved?: (call: RecentCallRecord) => void;
  onPatientContextSearchExhausted?: () => void;
  setPageNumber?: (page: number) => void;
  //For small mode with infinite scroll
  isInfiniteList?: boolean;
  scrollToIndex?: (index: number) => void;
}

interface UseCallInsightEligibleHotspotResult {
  callInsightHotspot?: RecentCallRecord;
  contactProfileHotspot?: RecentCallRecord;
  isSearching: boolean;
  pendingVisibleIndex: number | null;
  onItemVisible: (index: number) => void;
}

export function useCallInsightEligibleHotspot({
  tableData,
  currentPage,
  allPages,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  setPageNumber,
  isInfiniteList,
  scrollToIndex,
  openCallInsightPanel,
  onSearchExhausted,
  onPatientContextResolved,
  onPatientContextSearchExhausted,
}: UseCallInsightEligibleHotspotParams): UseCallInsightEligibleHotspotResult {
  const [pendingVisibleIndex, setPendingVisibleIndex] = useState<number | null>(null);

  const pendingActionRef = useRef<(() => void) | null>(null);

  const { completeHotspot, flows, completedHotspots } = useOnboardingShallowStore(
    'completeHotspot',
    'flows',
    'completedHotspots'
  );

  const { pendingTask, clearPendingTask, setEligibility } = useOnboardingCallActionShallowStore(
    'pendingTask',
    'clearPendingTask',
    'setEligibility'
  );

  const { selectedLocationIds } = useAppScopeStore();

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const { setShow } = useSlidePanelShallowStore('setShow');

  const { register: registerInDemoPanel } = useDemoPanelShallowStore('register');

  const flowRegistered = useMemo(() => flows.some((flow) => flow.id === CI_BASE_ONBOARDING.id), [flows]);

  const canShowCallInsightHotspot =
    flowRegistered && !completedHotspots.has(CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_HISTORY_CALL_INSIGHT_BUTTON);

  const canShowContactProfileHotspot =
    flowRegistered && !completedHotspots.has(CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR);

  const callInsightHotspot = useMemo(
    () => (canShowCallInsightHotspot ? findBestEligibleCall(tableData) : undefined),
    [canShowCallInsightHotspot, tableData]
  );

  const contactProfileHotspot = useMemo(
    () => (canShowContactProfileHotspot ? findBestEligibleCallWithContact(tableData) : undefined),
    [canShowContactProfileHotspot, tableData]
  );

  const bestCallSearch = usePaginatedItemSearch<RecentCallRecord>({
    allPages,
    isEnabled:
      pendingTask != null &&
      [
        OnboardingBaseTask.BASE_TASK_REVIEW_CONVERSATION_ANALYSIS,
        OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      ].includes(pendingTask),
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    findItem: findBestEligibleCall,
  });

  const bestContactCallSearch = usePaginatedItemSearch<RecentCallRecord>({
    allPages,
    isEnabled: pendingTask === OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    findItem: findBestEligibleCallWithContact,
  });

  const bestCall = bestCallSearch.item;

  const bestCallWithContact = bestContactCallSearch.item;

  const searchExhausted = bestCallSearch.searchExhausted || bestContactCallSearch.searchExhausted;

  const isSearching = bestCallSearch.isSearching || bestContactCallSearch.isSearching;

  const navigateToItem = useCallback(
    (call?: HydratedCallRecord) => {
      if (!call) return -1;

      if (isInfiniteList) {
        const targetIndex = getItemIndex(call, allPages);

        if (targetIndex !== -1) {
          scrollToIndex?.(targetIndex);
        }

        return targetIndex;
      }

      const targetPage = getItemPageNumber(call, allPages);

      if (targetPage !== -1 && targetPage !== currentPage) {
        setPageNumber?.(targetPage);
      }

      return -1;
    },
    [isInfiniteList, allPages, currentPage, setPageNumber, scrollToIndex]
  );

  const queueVisibleAction = useCallback((index: number, callback: () => void) => {
    pendingActionRef.current = callback;
    setPendingVisibleIndex(index);
  }, []);

  const executeAction = useCallback(
    (targetIndex: number, callback: () => void) => {
      if (isInfiniteList && targetIndex !== -1) {
        queueVisibleAction(targetIndex, callback);
      } else {
        callback();
      }
    },
    [isInfiniteList, queueVisibleAction]
  );

  const onItemVisible = useCallback(
    (index: number) => {
      if (pendingVisibleIndex !== index) return;

      const callback = pendingActionRef.current;

      pendingActionRef.current = null;

      setPendingVisibleIndex(null);

      callback?.();
    },
    [pendingVisibleIndex]
  );

  const handleSearchExhausted = useCallback(
    (task: OnboardingBaseTask) => {
      setEligibility({
        hasEligibleCall: false,
      });
      onSearchExhausted?.(task);
      clearPendingTask();
    },
    [setEligibility, onSearchExhausted, clearPendingTask]
  );

  useEffect(() => {
    if (pendingTask == null || isFetching) return;

    switch (pendingTask) {
      case OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS: {
        if (!bestCall) {
          if (searchExhausted) {
            handleSearchExhausted(pendingTask);
          }

          return;
        }

        const targetIndex = navigateToItem(bestCall);

        executeAction(targetIndex, () => {
          openCallInsightPanel?.(
            {
              hydratedCall: bestCall,
            },
            {
              defaultAccordion: 'callIntel',
            }
          );
        });

        clearPendingTask();

        return;
      }

      case OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT: {
        if (!bestCallWithContact) {
          if (searchExhausted) {
            completeHotspot(CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR);

            if (onPatientContextSearchExhausted) {
              onPatientContextSearchExhausted();
            } else {
              const demoLocationId = selectedLocationIds[0];

              if (demoLocationId) {
                registerInDemoPanel({
                  id: 'demo-contact-panel',
                  component: DemoContactPanelComponent,
                  componentProps: {
                    demoLocationId,
                    demoFeatures: ['patient-insights', 'call-intel-insights'],
                  },
                  width: WIDTH_DIMS.medium,
                });
              }
            }

            clearPendingTask();
          }

          return;
        }

        completeHotspot(CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR);

        const targetIndex = navigateToItem(bestCallWithContact);

        executeAction(targetIndex, () => {
          if (onPatientContextResolved) {
            onPatientContextResolved(bestCallWithContact);

            return;
          }

          const personId = bestCallWithContact.personId;

          if (personId) {
            setPersonId(personId, undefined, bestCallWithContact.locationId);

            setShow(true, 'contact');
          }
        });

        clearPendingTask();

        return;
      }

      default:
        return;
    }
  }, [
    pendingTask,
    isFetching,
    bestCall,
    bestCallWithContact,
    searchExhausted,
    navigateToItem,
    executeAction,
    openCallInsightPanel,
    completeHotspot,
    clearPendingTask,
    handleSearchExhausted,
    onPatientContextResolved,
    onPatientContextSearchExhausted,
    selectedLocationIds,
    registerInDemoPanel,
    setPersonId,
    setShow,
  ]);

  return {
    callInsightHotspot,
    contactProfileHotspot,
    isSearching,
    pendingVisibleIndex,
    onItemVisible,
  };
}
